/* eslint-disable camelcase */

/**
 * @description category types keys for the PLP page for both new and old navigation.
 */
const categoryTypes = {
  painting: [
    'painting-acrylic',
    'painting-gouache',
    'painting-oil',
    'painting-watercolour',
    'painting-other',
    'painting-spray-paint',
    'painting-mixed-media',
  ],
  printmaking: [
    'printmaking-monoprint',
    'printmaking-screenprints',
    'printmaking-lithographs',
    'printmaking-etchings-engravings',
    'printmaking-linocuts',
    'printmaking-woodcuts',
    'printmaking-collagraphs',
    'printmaking-other',
  ],
  prints: [
    'prints-monoprint',
    'prints-screenprint',
    'prints-lithographs',
    'prints-etchings-engravings',
    'prints-linocuts',
    'prints-woodcuts',
    'prints-collagraphs',
    'prints-c-type',
    'prints-giclee',
    'prints-other',
  ],
  sculpture: [
    'sculpture-stone',
    'sculpture-clay',
    'sculpture-wood',
    'sculpture-mixed-media',
    'sculpture-relief',
    'sculpture-bronze',
    'sculpture-other',
  ],
  photography: ['photography-colour', 'photography-black-and-white', 'photography-manipulated'],
  drawing: ['drawing-charcoal', 'drawing-ink', 'drawing-pastel', 'drawing-pencil', 'drawing-other'],
  digital_art: ['digital-art-giclee', 'digital-art-c-type', 'digital-art-other'],
};

const replaceParams = {
  digital_art: 'digital-art',
};

const baseCheck = {
  number: {
    convert: (value) => Number(value),
    isUnique: true,
    isValid: (value) => {
      return Number.isInteger(Number(value)) && Number(value) > 0;
    },
  },
  trueOnly: {
    convert: (value) => value === 'true',
    isUnique: true,
    isValid: (value) => {
      return value === 'true';
    },
  },
};

/**
 * @description generates an object containing all accepted keys for the PLP page based on the current navigation type.
 * - Product category, is based on the current navigation type.
 * - New navigation: Product category has Prints and the rest of the categories.
 * - Old navigation: Product category has Printmaking and Digital Art and the rest of the categories.
 * @param {object} params - The parameters for the function.
 * @return {object} An object containing all accepted keys for the PLP page.
 */
export const allAcceptedKeys = {
  sort: ['best_match', 'price_asc', 'price_desc', 'newest'],
  product_category: ['painting', 'prints', 'sculpture', 'photography', 'drawing', 'collage'],
  subject: [
    'abstract-conceptual',
    'animals-birds',
    'architecture-cityscapes',
    'flowers-plants',
    'landscapes-sea-sky',
    'nudes-erotic',
    'people-portraits',
    'still-life',
    'transportation-maps',
  ],
  style: [
    'abstract',
    'abstract-organic',
    'abstract-geometric',
    'collage',
    'photorealistic',
    'impressionistic',
    'expressive-and-gestural',
    'graphic-illustrative-and-typographic',
    'graphic-illustrative-and-typographic-cartoon',
    'graphic-illustrative-and-typographic-graphic',
    'graphic-illustrative-and-typographic-illustrative',
    'graphic-illustrative-and-typographic-typographic',
    'surrealistic',
    'urban-and-pop',
    'naive',
    'unspecified',
  ],
  in_sale: ['on', '20', '30', '50'],
  size_: ['small', 'medium', 'large', 'xlarge'],
  painting: categoryTypes.painting,
  printmaking: categoryTypes.printmaking,
  prints: categoryTypes.prints,
  sculpture: categoryTypes.sculpture,
  photography: categoryTypes.photography,
  drawing: categoryTypes.drawing,
  digital_art: categoryTypes.digital_art,
  shape: ['portrait', 'landscape', 'square'],
};

/**
 * @description generate object containing all accepted URL parameters for the PLP page.
 * based on the current navigation type.
 * - new: Prints and the rest of the categories and filter params.
 * - old: Printmaking and Digital Art and the rest of the categories and filter params.
 * @return {object} An object containing all accepted URL parameters for the PLP page.
 */
export const acceptedUrlParams = {
  colours: {
    convert: (value) => value,
    isUnique: false,
    isValid: (value) => {
      return value.length <= 6;
    },
  },
  sort: {
    convert: (value) => value,
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.sort.includes(value);
    },
  },
  product_category: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.product_category.includes(value);
    },
  },
  subject: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.subject.includes(value);
    },
  },
  style: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.style.includes(value);
    },
  },
  price_min: {
    ...baseCheck.number,
  },
  price_max: {
    ...baseCheck.number,
  },
  in_sale: {
    convert: (value) => (value === 'on' ? 'on' : Number(value)),
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.in_sale.includes(value);
    },
  },
  free_shipping: {
    ...baseCheck.trueOnly,
  },
  framed_or_ready_to_hang: {
    ...baseCheck.trueOnly,
  },
  size_: {
    convert: (value) => value,
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.size_.includes(value);
    },
  },
  has_fine_art_prints: {
    ...baseCheck.trueOnly,
  },
  height_min: {
    ...baseCheck.number,
  },
  height_max: {
    ...baseCheck.number,
  },
  width_min: {
    ...baseCheck.number,
  },
  width_max: {
    ...baseCheck.number,
  },
  page: {
    ...baseCheck.number,
  },
  q: {
    convert: (value) => value,
    isUnique: true,
    isValid: (value) => {
      return value.length > 0;
    },
  },
  product_boost: {
    convert: (value) => value,
    isUnique: true,
    isValid: (value) => {
      return value.length > 0;
    },
  },
  multiple_product_boost: {
    convert: (value) => {
      const validInput = value
        .split('-')
        .filter((id) => Number.isInteger(Number(id)) && Number(id) > 0);
      return validInput.join('-');
    },
    isUnique: true,
    isValid: (value) => {
      const ids = value.split('-');
      let wrongValues = 0;
      for (const id of ids) {
        if (!Number.isInteger(Number(id)) || Number(id) < 1) {
          wrongValues++;
        }
      }
      return wrongValues === ids.length ? false : true;
    },
  },
  painting: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.painting.includes(value);
    },
  },
  prints: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.prints.includes(value);
    },
  },
  sculpture: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.sculpture.includes(value);
    },
  },
  photography: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.photography.includes(value);
    },
  },
  drawing: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.drawing.includes(value);
    },
  },
  shape: {
    convert: (value) => [value],
    isUnique: true,
    isValid: (value) => {
      return allAcceptedKeys.shape.includes(value);
    },
  },
  ships_to: {
    convert: (value) => value,
    isUnique: true,
    isValid: (value) => {
      return value.length > 0;
    },
  },
  ships_from: {
    convert: (value) => value,
    isUnique: true,
    isValid: (value) => {
      return value.length > 0;
    },
  },
  verified_advertising_safe: {
    ...baseCheck.trueOnly,
  },
};

const hiddenParams = ['paginate', 'limit'];

const validateParams = (rawParams: string[]) => {
  const validParams = {};

  rawParams.forEach((param) => {
    const split = param.split(/-(.+)/).map((el) => el.replace(/\s+/g, ' ').trim());
    let key = split[0];
    const value = split[1];

    const validParam = () =>
      value && acceptedUrlParams.hasOwnProperty(key) && acceptedUrlParams[key].isValid(value);
    const inObject = () => validParams.hasOwnProperty(key);
    const attachable = () => !acceptedUrlParams[key].isUnique;
    const targetIsArray = () =>
      Array.isArray(validParams[key]) && !validParams[key].includes(value);
    const targetIsString = () => typeof validParams[key] === 'string' && value !== validParams[key];

    if (validParam()) {
      const validatedValue = acceptedUrlParams[key].convert(value);
      if (Object.keys(replaceParams).includes(key)) key = replaceParams[key];

      if (inObject()) {
        if (attachable()) {
          if (targetIsString()) {
            validParams[key] = [validParams[key], validatedValue];
          } else if (targetIsArray()) {
            validParams[key] = [...validParams[key], validatedValue];
          }
        }
      } else {
        validParams[key] = validatedValue;
      }
    }
  });

  return validParams;
};

const prefix = (paramsObject: Record<string, any>): string[] => {
  const filteredArray = hideSomeParams(paramsObject);
  const prefixedArray = filteredArray.flatMap(([key, value]) =>
    (Array.isArray(value) ? value : [value]).map((value) => {
      key = Object.values(replaceParams).includes(key)
        ? (Object.keys(replaceParams).find((k) => replaceParams[k] === key) ?? key)
        : key;
      return `${key}-${value}`;
    }),
  );
  return prefixedArray;
};

const hideSomeParams = (paramsObject: Record<string, any>) => {
  const filteredArray = Object.entries(paramsObject).filter(([key, value]) => {
    if (
      hiddenParams.includes(key) ||
      (key === 'page' && value == '1') ||
      (key === 'sort' && value == 'best_match')
    ) {
      return false;
    }
    return true;
  });
  return filteredArray;
};

export {prefix, validateParams};
